import { useState, useEffect } from "react";
import { MainProps, MicroLessonFilter } from "../types";
import { fetchMicroLessons, normalizeSearchResponse } from "../utils";
import { PAGE_SIZE } from "../constants";
import { Search } from "./Search";
import { SearchResults } from "./SearchResults";
import { Pagination } from "./Pagination";
import { ButtonGroup } from "./ButtonGroup";
import { ISearchItemResponse } from "../../../../models/ISearchLessonResponse";
import { useLessonStore } from "../../../../store/lesson.store";

export const Main = ({
  onCancel,
  onAdd,
  selectedMicroLessons,
  setSelectedMicroLessons,
  filters,
  setFilters,
}: MainProps) => {
  const [microLessons, setMicroLessons] = useState<ISearchItemResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const lessonId = useLessonStore((state) => state.lessonId);

  const getMicroLessons = async (filters: MicroLessonFilter[], pageNumber = 1) => {
    setIsLoading(true);
    try {
      const response = await fetchMicroLessons(filters, pageNumber);
      if (response.isSuccess) {
        setMicroLessons(normalizeSearchResponse(response.data.data, { lessonId }));
        const totalRecords = response.data.totalRecords || 0;
        setTotalPages(Math.max(1, Math.ceil(totalRecords / PAGE_SIZE)));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setSelectedMicroLessons([]);
      getMicroLessons(filters, currentPage);
    };
    fetchData();
  }, []);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    getMicroLessons(filters, page);
  };

  const handleFilterChange = (newFilters: MicroLessonFilter[]) => {
    setFilters(newFilters);
    setCurrentPage(1);
    getMicroLessons(newFilters, 1);
  };

  const handleAddMicroLesson = (microLesson: ISearchItemResponse) => {
    setSelectedMicroLessons([...selectedMicroLessons, microLesson]);
  };

  const handleRemoveMicroLesson = (microLesson: ISearchItemResponse) => {
    setSelectedMicroLessons(selectedMicroLessons.filter((lesson) => lesson.lessonId !== microLesson.lessonId));
  };

  const handleAdd = () => {
    onAdd(selectedMicroLessons);
  };

  return (
    <div className="main-container">
      <Search filters={filters} setFilters={handleFilterChange} isLoading={isLoading} />
      <SearchResults
        onAddMicroLesson={handleAddMicroLesson}
        onRemoveMicroLesson={handleRemoveMicroLesson}
        selectedMicroLessons={selectedMicroLessons}
        isLoading={isLoading}
        data={microLessons}
      />
      <div className="main-container-footer">
        {!isLoading && totalPages > 1 && (
          <Pagination totalPages={totalPages} currentPage={currentPage} onPageChange={handlePageChange} />
        )}
        <ButtonGroup onCancel={onCancel} onAdd={handleAdd} selectedMicroLessons={selectedMicroLessons} />
      </div>
    </div>
  );
};
